import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './card';
import { FileText, Clock, Calendar, ChevronRight, ArrowRight, FileCheck, FileClock, FileWarning } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from './badge';

interface TransactionTimelineProps {
  documentFlow?: {
    timeline: Array<{
      date: string;
      documentType: string;
      documentId: string;
      issuingOrganization: string;
      receivingOrganization: string;
      description: string;
      nextSteps: string[];
      previousSteps: string[];
    }>;
    lifecycleStage: string;
  };
  className?: string;
}

function sourceDate(value: unknown): { label: string; timestamp: number | null } {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw || /^(?:unknown(?: date)?|date unknown|not (?:specified|provided|available|recorded|determined)|n\/?a|none|null|undefined|tbd|pending|invalid date|[-–—?.]+)$/i.test(raw)) {
    return { label: 'Date unavailable', timestamp: null };
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const date = new Date(`${raw}T00:00:00Z`);
    if (Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === raw) {
      return { label: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }), timestamp: date.getTime() };
    }
  }
  return { label: `${raw} (unverified value)`, timestamp: null };
}

export function TransactionTimeline({ documentFlow, className }: TransactionTimelineProps) {
  console.log('TransactionTimeline Component Rendered. Received props:', { documentFlow });
  console.log('TransactionTimeline DETAILED: ', { 
    hasDocumentFlow: !!documentFlow,
    hasTimeline: !!documentFlow?.timeline,
    timelineLength: documentFlow?.timeline?.length || 0,
    timelineArray: Array.isArray(documentFlow?.timeline),
    timelineFirst: documentFlow?.timeline?.[0] ? {...documentFlow.timeline[0]} : null,
    lifecycleStage: documentFlow?.lifecycleStage
  });

  if (!documentFlow || !documentFlow.timeline || documentFlow.timeline.length === 0) {
    console.log('TransactionTimeline: Condition failed - rendering "No transaction timeline available".');
    return (
      <Card className={cn("w-full", className)}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Transaction Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
          <p>No transaction timeline available</p>
        </CardContent>
      </Card>
    );
  }

  // Sort valid calendar dates only; retain other generated values in supplied order.
  const sortedTimeline = documentFlow.timeline.map(item => ({ item, date: sourceDate(item.date) })).sort((a, b) => {
    if (a.date.timestamp === null) return b.date.timestamp === null ? 0 : 1;
    if (b.date.timestamp === null) return -1;
    return a.date.timestamp - b.date.timestamp;
  });

  // Get document type icon
  const getDocumentIcon = (documentType: string) => {
    const typeLower = documentType.toLowerCase();
    if (typeLower.includes('invoice')) return <FileCheck className="h-5 w-5" />;
    if (typeLower.includes('quote') || typeLower.includes('quotation')) return <FileClock className="h-5 w-5" />;
    if (typeLower.includes('certificate')) return <FileWarning className="h-5 w-5" />;
    return <FileText className="h-5 w-5" />;
  };

  // Get lifecycle stage badge color
  const getLifecycleColor = (stage: string) => {
    if (!stage) return "bg-gray-100 text-gray-800";
    const stageLower = stage.toLowerCase();
    if (stageLower === 'completed' || stageLower === 'payment') return "bg-green-100 text-green-800";
    if (stageLower === 'fulfillment') return "bg-blue-100 text-blue-800";
    if (stageLower === 'ordering') return "bg-yellow-100 text-yellow-800";
    if (stageLower === 'quotation') return "bg-purple-100 text-purple-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Transaction Timeline
        </CardTitle>
        {documentFlow.lifecycleStage && (
          <Badge className={cn("gap-1", getLifecycleColor(documentFlow.lifecycleStage))}>
            {documentFlow.lifecycleStage}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-muted-foreground">AI-generated timeline. Dates, stages and next steps may be inferred; check the original documents.</p>
        <div className="relative">
          {sortedTimeline.map(({ item, date }, index) => (
            <div key={index} className="mb-8 last:mb-0">
              <div className="flex">
                {/* Left timeline connector */}
                <div className="relative mr-4">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center z-10 relative">
                    {getDocumentIcon(item.documentType)}
                  </div>
                  {/* Connector line */}
                  {index < sortedTimeline.length - 1 && (
                    <div className="absolute top-10 bottom-0 left-1/2 w-px bg-gray-200 -translate-x-1/2 h-full"></div>
                  )}
                </div>

                {/* Document details */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium">{item.documentType}</h3>
                    <div className="text-xs text-muted-foreground">{item.documentId}</div>
                  </div>

                  <div className="mb-2 text-sm flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">{date.label}</span>
                  </div>

                  {/* Organization flow */}
                  <div className="flex items-center gap-2 mb-2 text-sm">
                    <span>{item.issuingOrganization}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{item.receivingOrganization}</span>
                  </div>

                  {/* Description */}
                  {item.description && (
                    <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                  )}

                  {/* Relationships */}
                  {(item.previousSteps?.length > 0 || item.nextSteps?.length > 0) && (
                    <div className="bg-muted/30 p-2 rounded-md text-xs mt-2">
                      {item.previousSteps?.length > 0 && (
                        <div className="mb-1">
                          <span className="text-muted-foreground">Suggested previous steps: </span>
                          {item.previousSteps.map((step, i) => (
                            <span key={i} className="font-medium">
                              {step}{i < item.previousSteps.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {item.nextSteps?.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Suggested next steps: </span>
                          {item.nextSteps.map((step, i) => (
                            <span key={i} className="font-medium">
                              {step}{i < item.nextSteps.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
